import React, { ReactElement } from "react";
import { Navigate } from "react-router-dom";
import { canChangeCountrySetting } from "../../components/CountrySettings/CountryAccessControlUtils";
import { APP_ROUTES, USER_TYPES } from "../../config/constants";
import { isLoginRequired } from "../../utils/auth";

interface CustomRouteProps {
  children: ReactElement;
  userProfile?: any;
  userRole?: string;
  path?: string;
}

export function PrivateRoute({ children }: Readonly<CustomRouteProps>): ReactElement {
  return isLoginRequired() ? <Navigate to="/login" replace /> : children;
}

interface SpecializedRouteWrapperProps extends CustomRouteProps {
  accessCheck?: (props: CustomRouteProps) => boolean;
}

function SpecializedRouteWrapper({ children, accessCheck, ...rest }: Readonly<SpecializedRouteWrapperProps>): ReactElement | null {
  if (isLoginRequired()) {
    return <Navigate to="/login" replace />;
  }

  if (accessCheck && !accessCheck(rest as CustomRouteProps)) {
    return null; // Or <Navigate to="/unauthorized" replace /> or a custom UnauthorizedComponent
  }

  return React.cloneElement(children, rest);
}

export function RuleEngineRoute(props: Readonly<CustomRouteProps>): ReactElement | null {
  return <SpecializedRouteWrapper {...props} />;
}

export function PosRoute(props: Readonly<CustomRouteProps>): ReactElement | null {
  const accessCheck = ({ userRole, path }: CustomRouteProps): boolean => {
    return !(userRole === USER_TYPES.READONLY_USER && path === APP_ROUTES.CREATE_POS);
  };
  return <SpecializedRouteWrapper {...props} accessCheck={accessCheck} />;
}

export function CountryRoute(props: Readonly<CustomRouteProps>): ReactElement | null {
  const accessCheck = ({ userProfile, path }: CustomRouteProps): boolean => {
    return !(canChangeCountrySetting(userProfile) && path === APP_ROUTES.RULE_ENGINE);
  };
  return <SpecializedRouteWrapper {...props} accessCheck={accessCheck} />;
}

export function DeliveryFeeRoute(props: Readonly<CustomRouteProps>): ReactElement | null {
  return <SpecializedRouteWrapper {...props} />;
}